/**
 * 스크립트 실행 오케스트레이션 — 결정 #4/#17. 캐릭터에 연결된 JS 스크립트를
 * 특정 훅에 대해 실행하고, 변환된 텍스트를 돌려준다.
 *
 * capability API(결정 #17)는 여기서 구성한다:
 *  - getVar/setVar: chat.extensions.scriptVars에 영속(scope별 네임스페이스).
 *  - getChatMessages: 최근 메시지 읽기(읽기 전용 스냅샷).
 *  - random: 시드 RNG(결정 #4 재현성) — chatId+hook 기반 시드.
 *  - now: Date.now.
 *  - elevated(network/storage 등)는 1차 버전 미주입(권한 게이팅으로 도달 불가).
 *
 * 보안: core의 isCapabilityAllowed/makeSeededRandom 정책을 그대로 따른다. 권한
 * 없는 기능은 API에 아예 넣지 않아 게스트에서 접근 불가(결정 #17).
 */
import {
  isCapabilityAllowed,
  makeSeededRandom,
  type Chat,
  type Message,
  type Script,
  type ScriptCapabilityApi,
  type ScriptHook,
} from "@roqeet/core";
import { storage } from "./storage.js";
import { scriptHost, fetchBroker } from "./script.js";

type VarScope = "chat" | "character" | "global";
type VarBag = Partial<Record<VarScope, Record<string, unknown>>>;

/** chat.extensions.scriptVars에서 변수 가방을 읽는다(없으면 빈 객체). */
function readVarBag(chat: Chat): VarBag {
  const ext = chat.extensions as { scriptVars?: VarBag } | undefined;
  return ext?.scriptVars ?? {};
}

/**
 * 캐릭터의 활성 JS 스크립트를 주어진 훅에 대해 순서대로 실행하고 변환된 텍스트를
 * 반환한다. 스크립트가 없거나 훅에 해당하는 게 없으면 입력을 그대로 돌려준다.
 *
 * @param chat       대화(변수 영속 + 시드 기준).
 * @param scripts    캐릭터에 연결된 스크립트(JS만 골라 처리).
 * @param hook       파이프라인 훅(on-user-send / after-response 등).
 * @param text       변환 대상 텍스트.
 * @param messages   현재 히스토리(getChatMessages용).
 */
export async function runScriptsForHook(
  chat: Chat,
  scripts: Script[],
  hook: ScriptHook,
  text: string,
  messages: Message[],
): Promise<string> {
  // JS(QuickJS)와 Lua(wasmoon) 둘 다 같은 capability 계약으로 실행한다(결정 #4/#6).
  // 호스트가 언어별 엔진으로 라우팅한다(CompositeScriptHost).
  const logicScripts = scripts.filter(
    (s) =>
      (s.language === "javascript" || s.language === "lua") &&
      s.enabled &&
      (s.hooks ?? []).includes(hook),
  );
  if (logicScripts.length === 0) return text;

  // 변수 가방을 메모리에 로드(실행 중 변경 후 마지막에 1회 영속).
  const bag: VarBag = structuredClone(readVarBag(chat));
  let dirty = false;
  let current = text;

  // 시드 RNG: 같은 대화/훅/메시지수면 재현 가능(결정 #4).
  const seed = hashSeed(`${chat.id}:${hook}:${messages.length}`);
  const random = makeSeededRandom(seed);

  const recentSnapshot = messages.map((m) => ({
    role:
      m.senderType === "user" ? "user" : m.senderType === "character" ? "assistant" : "system",
    content: m.variants[m.activeVariant]?.content ?? "",
    ...(m.senderName ? { name: m.senderName } : {}),
  }));

  for (const script of logicScripts) {
    const caps = script.capabilities;
    const consent = script.consentGranted ?? false;

    // 스토리지 가방(per-script 네임스페이스, 결정 #17): script.extensions.storage에
    // 영속한다. 다른 스크립트/앱 데이터와 격리되며 대화를 넘어 유지된다(global state).
    const storageBag: Record<string, unknown> = structuredClone(readStorageBag(script));
    let storageDirty = false;

    // capability API 구성. 권한 없는 기능은 넣지 않는다(결정 #17).
    const api: ScriptCapabilityApi = {
      getVar(scope, key) {
        if (!isCapabilityAllowed("variables", caps, consent)) return undefined;
        return bag[scope]?.[key];
      },
      setVar(scope, key, value) {
        if (!isCapabilityAllowed("variables", caps, consent)) return;
        (bag[scope] ??= {})[key] = value;
        dirty = true;
      },
      getChatMessages(limit) {
        const arr = recentSnapshot;
        return limit ? arr.slice(-limit) : arr;
      },
      setResult(t) {
        current = t;
      },
      log: (...args) => console.log("[script]", script.name, ...args),
      random,
      now: () => Date.now(),
    };

    // 중개 fetch(elevated, 결정 #17): network 권한 + 동의가 있을 때만 주입.
    // 권한 없으면 api.fetch는 undefined라 게스트에서 도달 불가.
    if (isCapabilityAllowed("network", caps, consent)) {
      const allowlist = caps?.network?.domains ?? [];
      api.fetch = (input, init) => fetchBroker.request(allowlist, input, init);
    }

    // 스토리지(elevated, 결정 #17): storage 권한 + 동의가 있을 때만 주입. 권한
    // 없으면 api.storageGet/Set은 undefined라 게스트에서 도달 불가.
    if (isCapabilityAllowed("storage", caps, consent)) {
      api.storageGet = async (key: string) => storageBag[key];
      api.storageSet = async (key: string, value: unknown) => {
        if (value === undefined) delete storageBag[key];
        else storageBag[key] = value;
        storageDirty = true;
      };
    }

    try {
      const loaded = await scriptHost.load(
        {
          id: script.id,
          language: script.language,
          source: script.source,
          hooks: script.hooks ?? [],
          capabilities: caps ?? {},
          consentGranted: consent,
        },
        api,
      );
      const out = await loaded.invoke(hook, { hook, text: current });
      loaded.dispose();
      if (typeof out.text === "string") current = out.text;
    } catch (e) {
      // 스크립트 실패는 격리 — 대화를 죽이지 않고 현재 텍스트 유지(결정 #4).
      console.error("스크립트 실행 실패", script.name, e);
    }

    // 이 스크립트의 스토리지가 바뀌었으면 per-script 네임스페이스에 영속(결정 #17).
    // 다른 스크립트와 격리되며 대화를 넘어 유지된다. 실패해도 대화는 계속.
    if (storageDirty) {
      try {
        const ext = { ...(script.extensions ?? {}), storage: storageBag } as Record<
          string,
          unknown
        >;
        await storage.scripts.put({ ...script, extensions: ext });
      } catch (e) {
        console.error("스크립트 스토리지 영속 실패", script.name, e);
      }
    }
  }

  // 변경된 변수만 영속(결정 #20: storage 경유로 sync 메타 갱신).
  if (dirty) {
    const ext = { ...(chat.extensions ?? {}), scriptVars: bag } as Record<string, unknown>;
    await storage.chats.put({ ...chat, extensions: ext });
  }

  return current;
}

/** script.extensions.storage에서 스토리지 가방을 읽는다(없으면 빈 객체, 결정 #17). */
function readStorageBag(script: Script): Record<string, unknown> {
  const ext = script.extensions as { storage?: Record<string, unknown> } | undefined;
  return ext?.storage ?? {};
}

/** 문자열 → 32bit 시드(djb2). 시드 RNG용(결정 #4 재현성). */
function hashSeed(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (h * 33) ^ s.charCodeAt(i);
  return h >>> 0;
}
